import qs from "qs";
import { Task } from "types/Models/task";
import {
  AdHocTask,
  NonAdHocTask,
  TaskTemplate,
  TaskTemplateResponse,
} from "types/Models/taskTemplate";
import { emptyApi } from "./emptyApi";
import { Product } from "./products";

export type UpdatedDeadlineResponse = {
  updated_deadline: string;
  deadline: string;
  is_fiscal: boolean;
  filing7004_filed: boolean;
  is_deadline_updated: boolean;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getTaskTemplates: build.query<{ templates: TaskTemplateResponse[] }, void>({
      query: () => `/api/inkle/tasks/templates/`,
    }),

    getBaseTaskTemplates: build.query<
      TaskTemplate,
      {
        searchTerm?: string;
        task_category_type?: "TAX,RND" | "BOOKS" | "INTRA_GROUP" | "SALES_TAX";
        active_status?: "INACTIVE" | "ACTIVE" | "ALL";
      }
    >({
      query: ({ searchTerm, task_category_type, active_status }) => {
        const queryParam = qs.stringify(
          { search_string: searchTerm, task_category_type, active_status },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `api/inkle/tasks/base_task_templates/${queryParam}`;
      },
    }),

    getSingleTaskTemplates: build.query<Product, { templateId: string }>({
      query: ({ templateId }) => {
        return `api/inkle/tasks/base_task_template/${templateId}`;
      },
    }),

    createTask: build.mutation<
      Task,
      { entity: string; metaData: AdHocTask | NonAdHocTask }
    >({
      query: ({ entity, metaData }) => {
        return {
          url: `/api/inkle/tasks/v1/company/entity/${entity}/task/`,
          method: "post",
          body: metaData,
        };
      },
      invalidatesTags: (result) => [
        { type: "TaskTemplate", id: "LIST" },
        "TaskList",
        "ALL_COMPLIANCES",
        "GROUP_FREE_TASK",
        "Tasks",
      ],
    }),

    getUpdatedDeadlines: build.query<
      UpdatedDeadlineResponse,
      { season: string; baseTaskTemplateId: string; entity: string }
    >({
      query: ({ baseTaskTemplateId, season, entity }) => {
        const queryParam = qs.stringify(
          { base_task_template_id: baseTaskTemplateId, season },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/tasks/company/entity/${entity}/get_updated_deadline/${queryParam}`,
        };
      },
    }),

    getGroupFreeTaskDetails: build.query<NonAdHocTask, { groupId: string }>({
      query: ({ groupId }) =>
        `/api/inkle/tasks/group/${groupId}/free-task-template/`,
      providesTags: ["GROUP_FREE_TASK"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetTaskTemplatesQuery,
  useGetTaskTemplatesQuery,
  useGetBaseTaskTemplatesQuery,
  useCreateTaskMutation,
  useGetSingleTaskTemplatesQuery,
  useLazyGetUpdatedDeadlinesQuery,
  useGetGroupFreeTaskDetailsQuery,
} = extendedApi;
